import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Download, ExternalLink, X } from "lucide-react";
import { BRAND } from "@/lib/site";

export default function PdfViewerModal({ isOpen, onClose, pdfUrl = BRAND.pdfMenuUrl }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Append timestamp to bypass browser & CDN HTTP caching for Supabase files
  const freshPdfUrl = pdfUrl
    ? `${pdfUrl}${pdfUrl.includes("?") ? "&" : "?"}v=${Date.now()}`
    : "";

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          data-testid="pdf-modal-wrapper"
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 md:p-8"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#1E140C]/75 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="pdf-modal-title"
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl h-[85vh] bg-[#FAF6F0] rounded-2xl md:rounded-3xl shadow-2xl border border-[#3B2818]/15 flex flex-col overflow-hidden z-10"
          >
            {/* Header / Toolbar */}
            <div className="px-5 py-4 sm:px-6 bg-[#F2EBE0] border-b border-[#3B2818]/10 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-full bg-[#C06A22]/15 text-[#C06A22] flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="truncate">
                  <h3 id="pdf-modal-title" className="font-serif text-lg md:text-xl text-[#3B2818] truncate">
                    Menu Catalog
                  </h3>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-[#6B5343]">
                    {BRAND.name} • Pune & Karad
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={freshPdfUrl}
                  download="Signature-Spoon-Menu.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.12em] bg-[#3B2818]/5 hover:bg-[#3B2818]/10 text-[#3B2818] transition-colors"
                  title="Download PDF"
                >
                  <Download className="h-3.5 w-3.5 text-[#C06A22]" />
                  <span>Download</span>
                </a>

                <a
                  href={freshPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.12em] bg-[#C06A22] hover:bg-[#A35618] text-[#FAF6F0] transition-colors shadow-sm"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="hidden xs:inline">Open Fullscreen</span>
                </a>

                <button
                  onClick={onClose}
                  aria-label="Close modal"
                  className="h-9 w-9 rounded-full border border-[#3B2818]/15 bg-white/50 hover:bg-white text-[#3B2818] flex items-center justify-center transition-colors active:scale-95 ml-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Main Viewer Area */}
            <div className="relative flex-1 bg-[#2C2621]/5 overflow-hidden flex flex-col">
              {pdfUrl ? (
                <iframe
                  src={`${freshPdfUrl}#toolbar=1`}
                  title="Signature Spoon Menu PDF"
                  className="w-full h-full border-0"
                  onError={() => console.warn("Failed to load PDF iframe")}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                  <FileText className="h-12 w-12 text-[#C06A22] mb-3 opacity-60" />
                  <h4 className="font-serif text-xl text-[#3B2818] mb-1">
                    Menu Catalog Coming Soon
                  </h4>
                  <p className="text-xs text-[#6B5343] max-w-sm mb-4">
                    Please upload your menu PDF to Supabase and set REACT_APP_MENU_PDF_URL in your environment.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
